import { useState, useEffect } from "react";
import {
  useParams, useDispatch, useSelector
} from "../../imports/generalImport";

import { Box, Container, Grid, Typography } from "../../imports/muiImport";
import { Helmet } from "react-helmet-async";

// API / Hooks
import { productDetailApiCall, } from "./productDetailHook";


// Redux
import {
  setProductDetails,
  wipeOutProductDetails,
} from "../../store/productInfo/action";
import {
  setNewDenomination,
  wipeOutDenomination,
} from "../../store/productDetails/action";

// Components
import PageBreadcrumb from "../../components/other/PageBreadcrumb";
import ProductDetailCard from "./productUtils/ProductDetailCard";
import PageNotFound from "../OTHERPAGE/PageNotFound";
import { actionNotifier } from "../../components/tostify";
import ProductDenomination from "./productUtils/ProductDenomination";
import GiftCardSkeleton from "../DASHBOARD/GiftCardSkeleton";
import SmilarGiftSectionComponent from "../../components/giftcardSections/similargift-section-component";

function ProductDetail() {
  const [productDetail, setProductDetail] = useState({});
  const [productFound, setProductFound] = useState(false);
  const [cpId, setCPId] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const PRODUCTDETAILS = useSelector((state) => state.ProductDetail?.allData);
  const { id: urlId } = useParams();
  const dispatch = useDispatch();

  const canonicalUrl = `https://www.99gift.ae/card-details/${productDetail?.slug}`;

  // -------------------------------
  // LOAD PRODUCT ON URL CHANGE
  // -------------------------------

  useEffect(() => {
    if (urlId !== cpId) {
      dispatch(wipeOutProductDetails());
      dispatch(wipeOutDenomination());
      getProductById(urlId);
      setCPId(urlId);
    }
  }, [urlId]);


  // -------------------------------
  // PAGE NOT FOUND
  // -------------------------------
  if (productFound) return <PageNotFound />;

  // -------------------------------
  // PAGE SEO HEAD
  // -------------------------------
  const seoTitle = productDetail?.name
    ? productDetail?.name
    : `Buy ${productDetail?.name} - 99gift.ae`;

  const seoDescription =
    productDetail?.description ||
    `Get instant discounts on ${productDetail?.name} when you purchase them online through 99gift. Take advantage of great deals on ${productDetail?.name}, coupons, and promo codes.`;

  // -------------------------------
  // RENDER UI
  // -------------------------------
  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>

        <meta property="og:title" content={seoTitle} />
        <meta name="description" content={seoDescription} />
        <meta name="og:description" content={seoDescription} />

        <meta property="og:url" content={canonicalUrl} />
        <link rel="canonical" href={canonicalUrl} />

        {productDetail?.metaSchema && (
          <script type="application/ld+json">
            {productDetail?.metaSchema}
          </script>
        )}

        <meta name="twitter:card" content="summary" />
        <meta
          name="twitter:title"
          content={productDetail?.metaSEO?.metaTitle}
        />
        <meta
          name="twitter:description"
          content={productDetail?.metaSEO?.metaDescription}
        />
        <meta name="twitter:image" content={productDetail?.image} />
        <meta name="twitter:url" content={canonicalUrl} />
      </Helmet>

      <Container maxWidth="lg" position="relative">
        <Grid
          container
          sx={{
            justifyContent: "center",
            rowGap: {
              xs: 2,
              md: 0
            },
            mt:{
              xs:0,
              md:6
            }
          }}
        >
          <Grid item xs={12} >
            <PageBreadcrumb home="Home" category={productDetail?.categories?.[0]?.slug} currentProduct={productDetail?.name} />
          </Grid>

          <Grid item xs={12} sm={12} md={6} lg={6} >
            <ProductDetailCard pageLoading={pageLoading} />
          </Grid>

          <Grid item xs={12} sm={12} md={6} lg={6}
            sx={{
              position: 'sticky',
              top: '90px',
              height: 'fit-content',
            }}
          >
            {PRODUCTDETAILS?.outOfStock ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: {
                    xs: '40px',
                    sm: '40px',
                    lg: '60px',
                  },
                  borderRadius: {
                    xs: 1,
                  },
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: {
                      xs: '1.2vw',
                      sm: '1.62vw',
                      lg: '1.8vw',
                    },
                    color: 'primary.main',
                    fontFamily: "Be Vietnam Pro"
                  }}
                >
                  Out of Stock
                </Typography>
              </Box>
            ) : (
              <ProductDenomination />
            )}
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ my: { xs: 2, md: 4 } }}>
        {PRODUCTDETAILS?.brandId ?
          <SmilarGiftSectionComponent
            sectionTitle={'Similar Gift Card'}
            sectionData={PRODUCTDETAILS?.brandId}
          />
          :
          <GiftCardSkeleton />
        }
      </Container>
    </>
  );

  // ===============================
  // FETCH PRODUCT FUNCTION
  // ===============================
  async function getProductById(urlId) {
    setPageLoading(true);

    try {
      const response = await productDetailApiCall(urlId);
      if (!response?.status) {
        setProductFound(true);
        actionNotifier.error(response?.message);
        return;
      }

      if (!response?.data?.rangeDenomination) {
        dispatch(setNewDenomination(response?.data?.newDeno));
      }

      setProductDetail(response?.data);
      dispatch(setProductDetails(response?.data));
    } catch (err) {
      console.error(err);
    }
    finally {
      setPageLoading(false);
    }
  }
}

export default ProductDetail;
